import { ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { NavigationEnd, Router, RouterOutlet } from "@angular/router";
import { slideInAnimation } from "@app/core/const";
import { HttpService, ProjectService, UserService } from "@app/core/services";
import { UserModel } from "@app/core/model";
import { DesignService } from "@app/core/services/design.service";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "@env/environment";
import { HttpResponse } from "@angular/common/http";
import { filter, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { ToastrService } from "ngx-toastr";
import { LocalStorageService } from "ngx-webstorage";

// import * as ruMessages from 'devextreme/localization/messages/ru.json';

@Component({
  selector: "app-body",
  templateUrl: "./body.component.html",
  styleUrls: ["./body.component.scss", "./body-modern.component.scss"],
  animations: [slideInAnimation],
})
export class BodyComponent implements OnInit, OnDestroy {
  public version = environment.version;
  public menuSizeClass = localStorage.getItem("menuSizeClass") || "big";
  public user: UserModel | null = null;
  public copied = false;
  public versionCopied = false;

  // Hover submenu popup state
  public hoveredMenu: { header: string; children: any[]; top: number } | null =
    null;
  isHomePage: boolean = false;
  // rus = ruMessages;
  isRequiredPersonalDataOpen: boolean = false;
  loginLoading: boolean = false;
  privacy: any = {
    lastName: null,
    middleName: null,
    firstName: null,
    email: null,
    phone: null,
    privacyOk: false,
  };
  // current design: 'classic' | 'modern'
  public design: "classic" | "modern" = this.designService.getDesign();
  private submenuTimeout: any = null;
  private ngUnsubscribe = new Subject();

  constructor(
    private httpService: HttpService,
    private projectService: ProjectService,
    private userService: UserService,
    // private translate: TranslateService,
    private ref: ChangeDetectorRef,
    private translateService: TranslateService,
    private toastr: ToastrService,
    // private breadcrumbService :BreadcrumbService,
    private router: Router,
    private designService: DesignService,
    private storage: LocalStorageService
  ) {
    // design service used to control modern background
    translateService.setDefaultLang("ru");
    // loadMessages(this.rus);
    // locale('ru');
  }

  get contentClass() {
    let mainMenu = document.getElementById("mainMenu");
    return {
      big: mainMenu?.classList.contains("big"),
      small: mainMenu?.classList.contains("small"),
    };
  }

  ngOnInit() {
    // profile data for modern header
    this.userService.getUserStream().subscribe((u) => (this.user = u));
    this.userService.isRequiredPersonalDataOpen.subscribe((b) => {
      this.isRequiredPersonalDataOpen = b;
      if (b) {
        console.log("ngOnInit body", this.router.url);
        if (this.router.url != "/home") this.router.navigate(["/home"]);
      }
    });

    // subscribe to design changes
    this.designService.design$.subscribe((d) => (this.design = d));

    console.log(this.router.url);
    this.isHomePage = /^\/?home(\/|$|\?|#)?/i.test(this.router.url);

    // Update `isHomePage` on every route change
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.isHomePage = /^\/?home(\/|$|\?|#)?/i.test(this.router.url);
        this.ref.detectChanges();
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  onChangeMenuSizeClass(e: string) {
    this.menuSizeClass = e;
    this.ref.detectChanges();
  }

  // Used by the modern layout collapse button
  toggleMenu() {
    this.menuSizeClass = this.menuSizeClass === "small" ? "big" : "small";
    try {
      localStorage.setItem("menuSizeClass", this.menuSizeClass);
    } catch {}
    this.ref.detectChanges();
  }

  // Submenu hover popup handlers
  onSubmenuHover(
    data: { header: string; children: any[]; top: number } | null
  ) {
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
    if (data) {
      // Calculate popup position - ensure it doesn't go below viewport
      const popupHeight = Math.min(data.children.length * 44 + 60, 400); // estimate height
      const viewportHeight = window.innerHeight;
      let top = data.top;

      // If popup would go below viewport, adjust position
      if (top + popupHeight > viewportHeight - 20) {
        top = Math.max(20, viewportHeight - popupHeight - 20);
      }

      this.hoveredMenu = { ...data, top };
    } else {
      this.hoveredMenu = null;
    }
    this.ref.detectChanges();
  }

  keepSubmenuOpen() {
    if (this.submenuTimeout) {
      clearTimeout(this.submenuTimeout);
      this.submenuTimeout = null;
    }
  }

  closeSubmenu() {
    this.submenuTimeout = setTimeout(() => {
      this.hoveredMenu = null;
      this.ref.detectChanges();
    }, 200); // increased delay for easier mouse transition
  }

  prepareRoute(outlet: RouterOutlet) {
    return (
      outlet &&
      outlet.activatedRouteData &&
      outlet.activatedRouteData["animation"]
    );
  }

  checkComparison = () => true;

  // Switch to classic design
  switchToClassicDesign() {
    this.designService.setDesign("classic");
  }

  // Logout user
  logout() {
    this.storage.clear("access_token");
    this.router.navigate(["/auth/login"]);
  }

  // Copy login to clipboard and show 1s tooltip
  copyLogin(evt?: Event) {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const login = this.user?.login || "";
    const done = () => {
      this.copied = true;
      this.ref.detectChanges();
      setTimeout(() => {
        this.copied = false;
        this.ref.detectChanges();
      }, 1000);
    };
    if (
      navigator &&
      (navigator as any).clipboard &&
      (navigator as any).clipboard.writeText
    ) {
      (navigator as any).clipboard
        .writeText(login)
        .then(done)
        .catch(() => done());
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = login;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {}
      done();
    }
  }

  // Copy version to clipboard and show 1s tooltip
  copyVersion(evt?: Event) {
    if (evt) {
      evt.preventDefault();
      evt.stopPropagation();
    }
    const ver = "v" + this.version;
    const done = () => {
      this.versionCopied = true;
      this.ref.detectChanges();
      setTimeout(() => {
        this.versionCopied = false;
        this.ref.detectChanges();
      }, 1000);
    };
    if (
      navigator &&
      (navigator as any).clipboard &&
      (navigator as any).clipboard.writeText
    ) {
      (navigator as any).clipboard
        .writeText(ver)
        .then(done)
        .catch(() => done());
    } else {
      try {
        const ta = document.createElement("textarea");
        ta.value = ver;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      } catch {}
      done();
    }
  }

  onSavePrivasy() {
    let body = {
      lastName: this.privacy.lastName,
      firstName: this.privacy.firstName,
      middleName: this.privacy.middleName,
      email: this.privacy.email,
      phoneNumber: this.privacy.phone,
    };
    this.httpService
      .postResponse("employee-service/api/employee/short", body)
      .subscribe(
        (response: HttpResponse<any>) => {
          if (response.ok) {
            this.translateService
              .get("notification.SUCCESS")
              .pipe(takeUntil(this.ngUnsubscribe))
              .toPromise()
              .then((v) => {
                this.toastr.success(v, "", { timeOut: 2000 });
              });
          }
          // this.isRequiredPersonalDataOpen = false;
          this.userService.isRequiredPersonalData.next(false);
          this.userService.isRequiredPersonalDataOpen.next(false);
        },
        (error: any) => {
          // this.dataGrids.instance.clearSelection();
          // Получаем перевод ошибки при отправке и показываем тостер
          this.translateService
            .get(["notification.FAILURE", "notification.ERROR_CODE"], {
              value: error.status,
            })
            .pipe(takeUntil(this.ngUnsubscribe))
            .toPromise()
            .then((v) => {
              this.toastr.error(
                error.error.message,
                v["notification.ERROR_CODE"],
                {
                  progressBar: false,
                  timeOut: 100000,
                  closeButton: true,
                }
              );
            });
        }
      );
  }
}
